import { RemoteData, rd } from '@passionware/monads';
import { ResourceType } from '__types__';
import { Callout } from '__types__/v1/models/Callout';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { CalloutDetailsFormValues } from 'v5/features/callouts/callout-create.types';
import { CalloutSlugField } from 'v5/features/callouts/CalloutSlugField';
import { ResourceTypeSelect } from 'v5/patterns/resource/ResourceTypeSelect';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

export interface CalloutDetailsFieldsProps<
  T extends CalloutDetailsFormValues = CalloutDetailsFormValues
> extends WithServices<
    [WithCalloutsService, WithAppScopeService, WithEnterpriseService]
  > {
  form: UseFormReturn<T>;
  resourceTypes: RemoteData<ResourceType[]>;
  syncSlugFromTitle?: boolean;
  excludeCalloutId?: Callout['id'];
  workspaceId?: number | null;
  showResourceTypeInfo?: boolean;
}

export function CalloutDetailsFields<
  T extends CalloutDetailsFormValues = CalloutDetailsFormValues
>({
  form,
  resourceTypes,
  syncSlugFromTitle = true,
  excludeCalloutId,
  workspaceId,
  showResourceTypeInfo = true,
  services
}: CalloutDetailsFieldsProps<T>) {
  const { t } = useTranslation('callouts');
  const titlePath = 'title' as FieldPath<T>;
  const resourceTypeIdPath = 'resource_type_id' as FieldPath<T>;

  return (
    <>
      <FormField
        control={form.control}
        name={titlePath}
        rules={{ required: true }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('CalloutCreateModal.title')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={t('CalloutCreateModal.titlePlaceholder')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <CalloutSlugField
        form={form}
        syncSlugFromTitle={syncSlugFromTitle}
        excludeCalloutId={excludeCalloutId}
        workspaceId={workspaceId}
        services={services}
      />
      <FormField
        control={form.control}
        name={resourceTypeIdPath}
        rules={{
          validate: value =>
            (value as number) > 0 ||
            t('CalloutCreateModal.resourceTypeRequired')
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('CalloutCreateModal.defaultResourceType')}</FormLabel>
            {showResourceTypeInfo && (
              <p className="text-sm text-fg-muted mb-2">
                {t('CalloutCreateModal.selectResourceTypeInfo')}
              </p>
            )}
            <FormControl>
              {rd
                .journey(resourceTypes)
                .wait(<Input disabled placeholder="Loading..." />)
                .catch(() => <Input disabled placeholder="Failed to load" />)
                .map(types => (
                  <ResourceTypeSelect
                    ref={field.ref}
                    value={field.value as number}
                    onValueChange={field.onChange}
                    resourceTypes={types}
                    placeholder={t(
                      'CalloutCreateModal.resourceTypePlaceholder'
                    )}
                  />
                ))}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
